import { AdminAsyncState } from '@/components/admin/admin-async-state';
import * as React from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'motion/react';
import { MapPin, type LucideIcon } from 'lucide-react';
import { EmptyState } from '@/components/admin/empty-state';
import {
  AdminToolbar,
  adminSelectableCardVariants,
} from '@/components/admin/admin-surface';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ComboboxField } from '@/components/ui/combobox';
import type { ComboboxOption } from '@/lib/combobox-option';
import { Input } from '@/components/ui/input';
import { SearchInput } from '@/components/ui/search-input';
import { Label } from '@/components/ui/label';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { pickerToneBorderClass, scopeRailTone } from '@/lib/admin-visual';
import { useBranding } from '@/contexts/branding-context';
import { useDealerTreeContext } from '@/contexts/dealer-tree-context';
import { EMPTY_BRANDING, globalApplicationOwnerLabel } from '@/lib/branding';
import type { DealerTreeDealer, DealerTreeSite } from '@/lib/admin-types';
import {
  adminScopeTreeListClass,
  adminScopeTreePanelBodyClass,
  adminScopeTreePanelClass,
  adminScopeTreeSlotClass,
} from '@/lib/admin-page-layout';

export type Selection =
  | { type: 'global'; id: 1; name: string }
  | { type: 'dealer'; id: number; name: string }
  | { type: 'site'; id: number; name: string };

export const GLOBAL_SELECTION_ID = 1;

/** Organization-wide scope row in the dealer/site tree (name follows runtime branding). */
export function buildGlobalSelection(brandName?: string | null): Selection {
  return { type: 'global', id: GLOBAL_SELECTION_ID, name: globalApplicationOwnerLabel(brandName) };
}

export const globalSelection: Selection = buildGlobalSelection(EMPTY_BRANDING.brandName);

import { stringifyError } from '@/lib/errors';

export { stringifyError };

export function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

/** Whether a site row is administratively disabled (`sites.is_active = 0`). */
export function isSiteAdministrativelyInactive(
  isActive: number | boolean | string | null | undefined,
): boolean {
  return isActive === 0 || isActive === false || isActive === '0';
}

export function SiteInactiveAdminIndicator({
  siteName,
  onOpenDetails,
}: {
  siteName?: string | null;
  onOpenDetails?: () => void;
}) {
  const label = siteName?.trim() || 'This site';
  const title = `${label} is inactive. Hidden from operators; scheduled delivery is paused. Turn on Active in Details to re-enable.`;
  const className = cn(
    'inline-flex max-w-full shrink-0 items-center gap-1.5 rounded-md border border-warning/35 bg-warning/15 px-2.5 py-1 text-xs text-warning-text',
    onOpenDetails &&
      'cursor-pointer underline-offset-2 transition-colors hover:bg-warning/25 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-warning/40',
  );
  const content = (
    <>
      <span className="font-semibold">Inactive</span>
      <span className="hidden min-w-0 truncate text-warning-text sm:inline">
        · hidden from operators
      </span>
    </>
  );

  if (onOpenDetails) {
    return (
      <button type="button" className={className} title={title} onClick={onOpenDetails}>
        {content}
      </button>
    );
  }

  return (
    <span className={className} title={title} role="status">
      {content}
    </span>
  );
}

/** @deprecated Use SiteInactiveAdminIndicator in the context toolbar instead. */
export function SiteInactiveAdminBanner({ siteName }: { siteName?: string | null }) {
  return <SiteInactiveAdminIndicator siteName={siteName} />;
}

export function useDealerTree() {
  const { dealers, loading, error, reload, invalidateDealerTree, treeRevision } = useDealerTreeContext();
  return { dealers, loading, error, reload, invalidateDealerTree, treeRevision };
}

export { useDealerTreeContext } from '@/contexts/dealer-tree-context';

type ScopeTreeProps = {
  dealers: DealerTreeDealer[];
  selection: Selection;
  onSelect: (selection: Selection) => void;
  loading?: boolean;
  error?: string | null;
  /** Optional root class (e.g. Sites Admin scope-tree polish). */
  className?: string;
};

function scopeSelectionTypeLabel(selection: Selection): string {
  if (selection.type === 'global') return selection.name;
  if (selection.type === 'dealer') return 'Dealer';
  return 'Site';
}

const SCOPE_TREE_LABEL_REVEAL_DELAY_MS = 1000;

function isScopeTreeLabelTruncated(el: HTMLElement | null): boolean {
  if (!el) return false;
  return el.scrollWidth > el.clientWidth + 1;
}

function scopeTreeLabelHasRevealContent(label: React.ReactNode): boolean {
  if (label == null || label === false) return false;
  if (typeof label === 'string') return label.trim().length > 0;
  return true;
}

/** Scroll containers between `node` and the document root (e.g. scope tree list). */
function getScrollableAncestors(node: HTMLElement | null): HTMLElement[] {
  const ancestors: HTMLElement[] = [];
  if (!node || typeof window === 'undefined') return ancestors;
  let current: HTMLElement | null = node.parentElement;
  while (current) {
    const { overflow, overflowX, overflowY } = window.getComputedStyle(current);
    const scrollable = (value: string) => /auto|scroll|overlay/.test(value);
    if (
      (scrollable(overflowY) || scrollable(overflowX) || scrollable(overflow)) &&
      (current.scrollHeight > current.clientHeight || current.scrollWidth > current.clientWidth)
    ) {
      ancestors.push(current);
    }
    current = current.parentElement;
  }
  return ancestors;
}

function ScopeTreeRow({
  label,
  selected,
  scopeType,
  onClick,
  compact = false,
  trailing,
}: {
  label: React.ReactNode;
  selected: boolean;
  scopeType: Selection['type'];
  onClick: () => void;
  compact?: boolean;
  trailing?: React.ReactNode;
}) {
  const labelRef = React.useRef<HTMLSpanElement>(null);
  const hoverTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const hoveringRef = React.useRef(false);
  const [revealOpen, setRevealOpen] = React.useState(false);
  const [position, setPosition] = React.useState<{ top: number; left: number; height: number } | null>(null);

  const canReveal = scopeTreeLabelHasRevealContent(label);
  const tone = scopeRailTone(scopeType === 'global' ? 'global' : scopeType === 'dealer' ? 'dealer' : 'site');
  const ariaLabel = typeof label === 'string' ? label : undefined;

  const updatePosition = React.useCallback(() => {
    const el = labelRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPosition({ top: rect.top, left: rect.left, height: rect.height });
  }, []);

  const clearHoverTimer = React.useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  }, []);

  const handlePointerEnter = React.useCallback(() => {
    hoveringRef.current = true;
    if (!canReveal) return;
    clearHoverTimer();
    hoverTimerRef.current = setTimeout(() => {
      if (!hoveringRef.current || !isScopeTreeLabelTruncated(labelRef.current)) return;
      updatePosition();
      setRevealOpen(true);
    }, SCOPE_TREE_LABEL_REVEAL_DELAY_MS);
  }, [canReveal, clearHoverTimer, updatePosition]);

  const handlePointerLeave = React.useCallback(() => {
    hoveringRef.current = false;
    clearHoverTimer();
    setRevealOpen(false);
    setPosition(null);
  }, [clearHoverTimer]);

  React.useLayoutEffect(() => {
    if (!revealOpen) return;
    updatePosition();
    const scrollParents = getScrollableAncestors(labelRef.current);
    const scrollOptions: AddEventListenerOptions = { capture: true, passive: true };
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, scrollOptions);
    for (const parent of scrollParents) {
      parent.addEventListener('scroll', updatePosition, scrollOptions);
    }
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, scrollOptions);
      for (const parent of scrollParents) {
        parent.removeEventListener('scroll', updatePosition, scrollOptions);
      }
    };
  }, [revealOpen, updatePosition]);

  React.useEffect(() => () => clearHoverTimer(), [clearHoverTimer]);

  const reveal =
    revealOpen && canReveal && position
      ? createPortal(
          <div
            role="tooltip"
            className={cn(
              'pointer-events-none fixed z-50 flex items-center whitespace-nowrap rounded-md border border-border shadow-md',
              compact ? 'px-2 py-1.5 text-sm' : 'px-2.5 py-2',
              selected
                ? cn(
                    adminSelectableCardVariants({ selected: true, interactive: false }),
                    'border-l-[3px]',
                    pickerToneBorderClass(tone),
                  )
                : 'bg-popover text-popover-foreground',
            )}
            style={{
              top: position.top,
              left: position.left,
              minHeight: position.height,
            }}
          >
            {label}
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      <button
        type="button"
        onClick={onClick}
        aria-current={selected ? 'true' : undefined}
        aria-label={ariaLabel}
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
        className={cn(
          'flex w-full min-w-0 items-center gap-2 rounded-lg text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          compact ? 'px-2 py-1.5 text-sm' : 'px-2.5 py-2',
          selected
            ? cn(
                adminSelectableCardVariants({ selected: true, interactive: true }),
                'admin-rail border-l-[3px] pl-3.5',
                pickerToneBorderClass(tone),
              )
            : 'hover:bg-muted/45',
        )}
      >
        <span ref={labelRef} className="block min-w-0 flex-1 truncate">
          {label}
        </span>
        {trailing}
      </button>
      {reveal}
    </>
  );
}

function ScopeTreePanel({
  dealers,
  selection,
  onSelect,
  loading = false,
  error,
  listMaxHeightClass = adminScopeTreeListClass,
  className,
}: ScopeTreeProps & { listMaxHeightClass?: string }) {
  const { branding } = useBranding();
  const globalScope = React.useMemo(
    () => buildGlobalSelection(branding.brandName),
    [branding.brandName],
  );
  const [query, setQuery] = React.useState('');
  const normalizedQuery = query.trim().toLowerCase();
  const filteredDealers = dealers
    .map((dealer) => {
      const sites = asArray<DealerTreeSite>(dealer.sites);
      if (!normalizedQuery) return { dealer, sites };
      const dealerMatches = dealer.name.toLowerCase().includes(normalizedQuery);
      const filteredSites = sites.filter((site) => site.name.toLowerCase().includes(normalizedQuery));
      return dealerMatches ? { dealer, sites } : { dealer, sites: filteredSites };
    })
    .filter(({ dealer, sites }) => !normalizedQuery || dealer.name.toLowerCase().includes(normalizedQuery) || sites.length > 0);

  return (
    <Card className={cn('min-w-0 max-w-full overflow-hidden', adminScopeTreePanelClass, className)}>
      <CardContent className={cn('min-w-0 space-y-2 pt-4', adminScopeTreePanelBodyClass)}>
        <AdminToolbar className="space-y-0 p-2">
          <SearchInput
            embedded
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search dealers or sites..."
            aria-label="Search dealers or sites"
          />
        </AdminToolbar>
        <ScopeTreeRow
          label={globalScope.name}
          selected={selection.type === 'global'}
          scopeType="global"
          onClick={() => onSelect(globalScope)}
        />
        <motion.div className={cn('min-w-0 space-y-3 overflow-x-hidden pr-1', listMaxHeightClass)}>
          {filteredDealers.map(({ dealer, sites }) => (
            <motion.div key={dealer.id} className="min-w-0 rounded-lg border border-border/50 bg-card/50 p-2">
              <ScopeTreeRow
                label={dealer.name}
                selected={selection.type === 'dealer' && selection.id === dealer.id}
                scopeType="dealer"
                onClick={() => onSelect({ type: 'dealer', id: dealer.id, name: dealer.name })}
              />
              <div className="mt-1 min-w-0 space-y-0.5 pl-2">
                {sites.map((site) => (
                  <ScopeTreeRow
                    key={site.id}
                    compact
                    label={site.name}
                    selected={selection.type === 'site' && selection.id === site.id}
                    scopeType="site"
                    onClick={() => onSelect({ type: 'site', id: site.id, name: site.name })}
                    trailing={
                      isSiteAdministrativelyInactive(site.is_active) ? (
                        <Badge className="shrink-0" variant="outline">
                          Inactive
                        </Badge>
                      ) : null
                    }
                  />
                ))}
              </div>
            </motion.div>
          ))}
          {normalizedQuery && !loading && !filteredDealers.length ? (
            <EmptyState variant="inline" title="No dealers or sites match" description="Try a different search term." />
          ) : null}
          {loading && !dealers.length ? (
            <AdminAsyncState status="loading" loadingLabel="Loading dealer tree" aria-label="Loading dealer tree" />
          ) : null}
          {error ? (
            <AdminAsyncState status="error" error={error} />
          ) : null}
          {!loading && !error && !normalizedQuery && !dealers.length ? (
            <EmptyState variant="inline" title="No dealers loaded yet" />
          ) : null}
        </motion.div>
      </CardContent>
    </Card>
  );
}

/** Dealer/site tree: inline sidebar at `lg+`, compact scope bar + sheet below `lg`. */
export function ScopeTree(props: ScopeTreeProps) {
  const [sheetOpen, setSheetOpen] = React.useState(false);
  const { selection, onSelect, className } = props;

  const handleSelect = React.useCallback(
    (next: Selection) => {
      onSelect(next);
      setSheetOpen(false);
    },
    [onSelect],
  );

  return (
    <>
      <div className="min-w-0 max-w-full lg:hidden">
        <Card className="min-w-0 max-w-full overflow-hidden">
          <CardContent className="flex min-w-0 items-center gap-3 p-4">
            <MapPin className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {scopeSelectionTypeLabel(selection)} scope
              </p>
              <p className="truncate font-medium leading-snug" title={selection.name}>
                {selection.name}
              </p>
            </div>
            <Button type="button" variant="outline" size="sm" className="shrink-0" onClick={() => setSheetOpen(true)}>
              Change
            </Button>
          </CardContent>
        </Card>
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen} analyticsFeatureId="surface.components.admin-shared.sheet-1">
          <SheetContent aria-label="Choose scope" side="left" className="flex w-[min(100vw,20rem)] max-w-full flex-col overflow-y-auto p-3 sm:max-w-sm">
            <div className="min-w-0">
              <ScopeTreePanel
                {...props}
                className={className}
                onSelect={handleSelect}
                listMaxHeightClass={cn(adminScopeTreeListClass, 'max-h-[calc(100dvh-6rem)] overflow-y-auto')}
              />
            </div>
          </SheetContent>
        </Sheet>
      </div>
      <div className={cn('hidden min-w-0 max-w-full lg:block', adminScopeTreeSlotClass)}>
        <ScopeTreePanel {...props} className={className} />
      </div>
    </>
  );
}

export function Field({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  const id = React.useId();
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} value={value} onChange={(event) => onChange(event.target.value)} />
    </div>
  );
}

const FALLBACK_TIME_ZONES = [
  'UTC',
  'America/Vancouver',
  'America/Edmonton',
  'America/Regina',
  'America/Winnipeg',
  'America/Toronto',
  'America/Montreal',
  'America/Halifax',
  'America/St_Johns',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'Europe/London',
  'Europe/Paris',
  'Asia/Tokyo',
] as const;

function getSupportedTimeZones() {
  const intlWithSupportedValues = Intl as typeof Intl & {
    supportedValuesOf?: (key: 'timeZone') => string[];
  };
  const values = intlWithSupportedValues.supportedValuesOf?.('timeZone');
  return values?.length ? values : [...FALLBACK_TIME_ZONES];
}

const TIME_ZONE_OPTIONS: ComboboxOption[] = getSupportedTimeZones().map((timeZone) => ({
  value: timeZone,
  label: timeZone,
  description: timeZone.replace(/_/g, ' ').replace(/\//g, ' / '),
  keywords: timeZone.split(/[\/_]/g),
}));

function timeZoneOptionsForValue(value?: string | null) {
  const normalized = String(value ?? '').trim();
  if (!normalized || TIME_ZONE_OPTIONS.some((option) => option.value === normalized)) return TIME_ZONE_OPTIONS;
  return [
    { value: normalized, label: normalized, description: 'Saved custom timezone' },
    ...TIME_ZONE_OPTIONS,
  ];
}

export function TimeZoneCombobox({
  label = 'Time zone',
  value,
  onChange,
  icon: Icon,
}: {
  label?: string;
  value?: string | null;
  onChange: (value: string) => void;
  icon?: LucideIcon;
}) {
  const id = React.useId();
  const normalizedValue = String(value ?? '').trim();
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="gap-1.5">
        {Icon ? <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden /> : null}
        {label}
      </Label>
      <ComboboxField
        id={id}
        options={timeZoneOptionsForValue(normalizedValue)}
        value={normalizedValue}
        onValueChange={onChange}
        placeholder="Select timezone..."
        searchPlaceholder="Search timezones..."
        emptyText="No timezones found."
      />
    </div>
  );
}
